import * as React from "react";
import { useEffect, useMemo, useState, useRef } from "react";
import { loadStripe } from "@stripe/stripe-js";
import { Elements } from "@stripe/react-stripe-js";
import { RootState } from "../../reducers/index";
import { useAppDispatch, useAppSelector } from "../../store/index";
import CheckoutClientHead from "./checkoutclienthead";
import {
    useGetPaymentConfigQuery,
    useStripePaymentSetupMutation,
} from "../../reducers/registerapi";
import {
    StripePaymentSetupRequest,
    StripePaymentSetupResponse,
} from "../../interfaces/payment";
import Loading from "../../components/loading";
import CheckoutPaymentStripe from "./checkoutpaymentstripe";

const CheckoutPayment = () => {
    const { items, description } = useAppSelector((state: RootState) =>
        state.cart
    );
    const { providerId, clientId } = useAppSelector((state: RootState) =>
        state.checkout
    );
    const { data: paymentData, isLoading: paymentIsLoading } =
        useGetPaymentConfigQuery({ id: providerId }, {});

    const [stripePaymentSetup, { isLoading, isUninitialized, data }] = useStripePaymentSetupMutation();
    const [paymentSetupResponse, setPaymentSetupResponse] = useState<StripePaymentSetupResponse | null>(null);

    // useRef flag to ensure our payment setup only runs once.
    // This is to avoid a problem in STRICT / DEV react mode
    const hasCalledSetup = useRef(false);

    useEffect(() => {
        const setupPayment = async () => {
            if (!hasCalledSetup.current &&
                isUninitialized && providerId && clientId && items &&
                description
            ) {
                hasCalledSetup.current = true;
                const stripePaymentSetupRequest: StripePaymentSetupRequest = {
                    providerId: providerId,
                    clientId: clientId,
                    cartItems: items,
                    cartDescription: description,
                };
                const response = await stripePaymentSetup(
                    stripePaymentSetupRequest,
                );
                if (response && response.data) {
                    console.log("Setup payment response", response.data);
                    setPaymentSetupResponse(response.data);
                } else {
                    throw new Error("Failed to setup payment");
                }
            }
        };
        setupPayment();
    }, [
        isUninitialized,
        stripePaymentSetup,
        providerId,
        clientId,
        items,
        description,
    ]);

    const stripePromise = useMemo(() => {
        if (paymentIsLoading || !paymentData) {
            return null;
        }
        console.log("stripe public key", paymentData);
        if (!paymentData.stripePublicKey) {
            console.error("Expected stripePublicKey", paymentData);
        }
        if (!paymentData.stripeConnectedAccount) {
            console.error("Expected stripeConnectedAccount", paymentData);
        }

        try {
            if (paymentData.stripePublicKey) {
                if (paymentData.stripeConnectedAccount) {
                    return loadStripe(paymentData.stripePublicKey, {
                        stripeAccount: paymentData.stripeConnectedAccount,
                    });
                } else {
                    return loadStripe(paymentData.stripePublicKey);
                }
            } else {
                return null;
            }
        }
        catch (error) {
            console.error("Error loading Stripe:", error);
            return null;
        }
    }, [paymentData, paymentIsLoading, providerId]);

    if (isLoading || isUninitialized || !paymentSetupResponse) {
        return (
            <>
                <CheckoutClientHead />
                <h3>Payment</h3>
                <Loading />
            </>
        );
    }

    const options = {
        clientSecret: paymentSetupResponse.clientSecret,
        // You can also include other options here, e.g., appearance settings.
    };

    return (
        <>
            <CheckoutClientHead />
            <h3>Payment</h3>
            <Elements stripe={stripePromise} options={options}>
                <CheckoutPaymentStripe receiptUrl={paymentSetupResponse.receiptUrl!} />
            </Elements>
        </>
    );
};

export default CheckoutPayment;
